import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccount, useAccountTransactions } from '../../hooks/useAccounts';
import { accountsService } from '../../services/accountsService';
import { formatCurrency } from '../../utils/currency';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import ResponsiveTable from '../../components/ui/ResponsiveTable';

const AccountDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { account, loading: accountLoading, error: accountError } = useAccount(id || null);
  const { transactions, loading: transactionsLoading, error: transactionsError } = useAccountTransactions(id || null);

  if (accountLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (accountError || !account) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
        {accountError || 'Account not found'}
      </div>
    );
  }

  const getTransactionTypeLabel = (amount: number): string => {
    return amount >= 0 ? 'Credit' : 'Debit';
  };

  const getTransactionTypeBadgeClass = (amount: number): string => {
    return amount >= 0 ? 'badge-success' : 'badge-danger';
  };

  const formatTransactionAmount = (amount: number): string => {
    const sign = amount >= 0 ? '+' : '';
    return `${sign}${formatCurrency(amount)}`;
  };

  return (
    <div>
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{account.name}</h1>
          <p className="text-gray-600 mt-1">
            <span className={`badge ${accountsService.getTypeBadgeClass(account.type)}`}>
              {accountsService.getTypeLabel(account.type)}
            </span>
          </p>
        </div>
        <div className="flex space-x-2">
          {account.is_editable && (
            <Button
              variant="outline"
              onClick={() => navigate(`/accounts/${account.id}/edit`)}
            >
              Edit Account
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => navigate('/accounts')}
          >
            Back to Accounts
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Account Information */}
        <div className="lg:col-span-2 card">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Account Information</h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Account ID</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">{account.account_id}</dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Account Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{account.name}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Account Type</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className={`badge ${accountsService.getTypeBadgeClass(account.type)}`}>
                    {accountsService.getTypeLabel(account.type)}
                  </span>
                </dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Current Balance</dt>
                <dd className="mt-1 text-lg font-medium text-gray-900">
                  {accountsService.getFormattedBalance(account.balance)}
                </dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {account.is_editable ? (
                    <span className="badge badge-success">Editable</span>
                  ) : (
                    <span className="badge badge-secondary">System Account</span>
                  )}
                </dd>
              </div>

              {/* Bank-specific information */}
              {account.type === 'bank' && (
                <>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">Bank Name</dt>
                    <dd className="mt-1 text-sm text-gray-900">{account.bank_name || 'N/A'}</dd>
                  </div>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">Account Number</dt>
                    <dd className="mt-1 text-sm text-gray-900">{account.account_number || 'N/A'}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Account Holder Name</dt>
                    <dd className="mt-1 text-sm text-gray-900">{account.account_holder_name || 'N/A'}</dd>
                  </div>
                </>
              )}

              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(account.created_at).toLocaleDateString()}
                </dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(account.updated_at).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Account Summary */}
        <div className="card">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Account Summary</h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Total Transactions</h4>
                <p className="mt-1 text-lg font-medium text-gray-900">
                  {transactions.length}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900">Total Credits</h4>
                <p className="mt-1 text-lg font-medium text-green-600">
                  {formatCurrency(transactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0))}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900">Total Debits</h4>
                <p className="mt-1 text-lg font-medium text-red-600">
                  {formatCurrency(Math.abs(transactions.filter(t => t.amount < 0).reduce((sum, t) => sum + t.amount, 0)))}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Transaction History</h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          {transactionsLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
            </div>
          ) : transactionsError ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              {transactionsError}
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No transactions found for this account.</p>
            </div>
          ) : (
            <ResponsiveTable
              headers={['Date', 'Description', 'Type', 'Amount', 'Reference']}
              rows={transactions.map(transaction => [
                new Date(transaction.created_at).toLocaleDateString(),
                transaction.description || 'N/A',
                <span className={`badge ${getTransactionTypeBadgeClass(transaction.amount)}`}>
                  {getTransactionTypeLabel(transaction.amount)}
                </span>,
                <span className={transaction.amount >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                  {formatTransactionAmount(transaction.amount)}
                </span>,
                transaction.reference_number || 'N/A'
              ])}
              renderRow={(row, index) => {
                const transaction = transactions[index];
                return (
                  <tr key={transaction.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {row[0]}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row[1]}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row[2]}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row[3]}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row[4]}
                    </td>
                  </tr>
                );
              }}
              renderMobileCard={(row, index) => {
                const transaction = transactions[index];
                return (
                  <Card key={transaction.id} className="mb-4">
                    <div className="px-4 py-5 sm:p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-gray-900">
                            {row[1]}
                          </h3>
                          <div className="mt-2 grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-gray-500">Date</p>
                              <p className="text-sm font-medium text-gray-900">{row[0]}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Type</p>
                              <p className="text-sm font-medium text-gray-900">{row[2]}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Amount</p>
                              <p className="text-sm font-medium text-gray-900">{row[3]}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Reference</p>
                              <p className="text-sm font-medium text-gray-900">{row[4]}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              }}
              emptyMessage="No transactions found"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountDetails;